import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useProject } from "../context/ProjectContext";
import { industryByKey } from "../lib/constants";
import { projectTab } from "../lib/flow";
import DrawingStudio from "../components/studio/DrawingStudio";
import { PenNib, Warning, Sparkle } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { EquipmentSwitcher } from "../components/EquipmentSwitcher";

export default function DrawingStudioPage({ embedded = false }) {
  const { projects, pid, setPid, loading } = useProject();
  const [live, setLive] = useState(null);

  const ordered = useMemo(() => {
    if (loading) return [];
    return projects;
  }, [projects, loading]);

  // the cached project list can be stale (design run after it was fetched) — ask the API
  useEffect(() => {
    if (!pid) return;
    setLive(null);
    api.get(`/projects/${pid}`).then(({ data }) => setLive(data)).catch(() => setLive(null));
  }, [pid]);

  const selected = live || ordered.find((p) => p.id === pid);
  const hasDesign = !!((selected?.design_results || {}).modules);

  // give the AI Copilot project context while in the studio
  useEffect(() => { if (pid) localStorage.setItem("fg_last_project", pid); }, [pid]);

  return (
    <div className={`flex flex-col ${embedded ? "h-[calc(100vh-16rem)] min-h-[34rem] border border-[var(--c-border)] rounded-lg overflow-hidden" : "h-full"}`} data-testid="ds-page">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--c-border)] bg-[var(--c-surface)] flex-wrap">
        {!embedded && (
          <>
            <div className="flex items-center gap-2">
              <PenNib size={17} className="text-[var(--c-accent)]" />
              <span className="text-sm font-medium text-[var(--c-text)]">Drawing Studio</span>
            </div>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="ds-project-select"
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs w-96 rounded-sm">
                <SelectValue placeholder="Select project…" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)] max-h-80">
                {ordered.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`ds-project-option-${p.id.slice(0, 8)}`}
                    className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">
                    {`${p.project_code} — ${(p.product_summary || "—").slice(0, 44)}${p.design_results?.modules ? "" : " · (no design yet)"}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <EquipmentSwitcher projectId={pid} onSwitched={() => window.location.reload()} />
            {selected && (
              <span className="font-mono text-[10px] text-[var(--c-muted)]" data-testid="ds-project-meta">
                {industryByKey(selected.industry)?.name || selected.industry} · stage: {selected.stage}
              </span>
            )}
          </>
        )}
        {embedded && (
          <div className="flex items-center gap-2">
            <PenNib size={16} className="text-[var(--c-accent)]" />
            <span className="text-xs font-medium text-[var(--c-text)]">Drawing Studio</span>
          </div>
        )}
        {!hasDesign && selected && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-[var(--c-amber)]" data-testid="ds-no-design-banner">
            <Warning size={13} weight="fill" /> No design results — drawings generate from design calculations.
            <Link to={projectTab(pid, "design")} data-testid="ds-design-link" className="underline hover:text-[var(--c-text)]">Run Design Engine</Link>
          </span>
        )}
        <button data-testid="ds-copilot-btn"
          onClick={() => window.dispatchEvent(new CustomEvent("fg:copilot-open"))}
          title="Ask the AI Copilot to change design parameters — 3D model, drawings, BOM and datasheet follow automatically"
          className="ml-auto h-8 px-3 rounded-sm bg-[var(--c-accent)]/15 border border-[var(--c-accent)]/50 text-[var(--c-accent)] text-xs flex items-center gap-1.5 hover:bg-[var(--c-accent)]/25 transition-colors duration-150">
          <Sparkle size={13} weight="fill" /> AI Copilot — design &amp; 3D changes
        </button>
      </div>
      <div className="flex-1 min-h-0">
        {pid ? <DrawingStudio key={pid} projectId={pid} /> : (
          <div className="p-8 text-xs text-[var(--c-muted)]" data-testid="ds-loading">Loading projects…</div>
        )}
      </div>
    </div>
  );
}
